import * as React from 'react';
import { AnnouncementBanner } from '@helpcenter/announcement-banner';
import { REQUEST_CREATE } from '@helpcenter/connect-fragment';
import { DocumentTitle } from '@helpcenter/document-title';
import { withErrorPage } from '@helpcenter/error-page';
import { isUseHelpCenterRefresh } from '@helpcenter/feature-flags';
import { FORGE_REQUEST_CREATE } from '@helpcenter/forge-ui-utils/src/constants';
import type { WrappedComponentProps } from 'react-intl-next';
import { injectIntl } from 'react-intl-next';
import { LazySuspense } from 'react-loosely-lazy';
import { di } from 'react-magnetic-di';
import { HeaderConnectFragments, FooterConnectFragments } from 'view/common/connect-fragments-on-all-pages';
import { LoadingContainer } from 'view/common/loading-container';
import { ForgeFooterModule } from 'view/forge-ui/portal-footer';
import { ForgeHeaderModule } from 'view/forge-ui/portal-header';
import { PaperContent } from 'view/layout/paper';
import { LazyPortalSettingsSidebar } from 'view/portal-settings-sidebar';
import { RequestCreateForm } from 'view/request-create-form';
import i18n from 'view/request-create-page/messages';
import { AnnouncementInnerContainer } from 'view/request-create-page/styled';
import { UiModificationsEntryPoint } from '@atlassian/jsm-ui-modifications-view-request-create/src/ui/entry-point';
import { getPortalSettingsRequestTypeDetailOrphanRoute } from '../portal-settings-sidebar/common';
import { HeaderFocusMode } from './header-focus-mode';

export interface RequestCreatePageProps {
    loading: boolean;
    requestTypeName: string | undefined;
    portalName: string | undefined;
    announcement: { userLanguageHeader: string; userLanguageMessageWiki: string } | undefined;
    projectId: number;
    portalId: number | undefined;
    requestTypeId: number;
    isProjectSimplified: boolean;
    isProjectAdmin: boolean;
    sideBarOpen: boolean;
    toggleSidebar: (value?: boolean) => void;
}

export const RequestCreatePage: React.FunctionComponent<RequestCreatePageProps & WrappedComponentProps> = ({
    loading,
    requestTypeName,
    portalName,
    intl,
    announcement,
    portalId,
    projectId,
    requestTypeId,
    isProjectSimplified,
    isProjectAdmin,
    sideBarOpen,
    toggleSidebar,
}) => {
    di(UiModificationsEntryPoint, RequestCreateForm);

    const openSidebar = React.useCallback(() => toggleSidebar(true), [toggleSidebar]);
    const closeSidebar = React.useCallback(() => toggleSidebar(false), [toggleSidebar]);

    const title =
        requestTypeName &&
        portalName &&
        intl.formatMessage(i18n.title, {
            requestTypeName,
            portalName,
        });

    return (
        <DocumentTitle title={title} ignoreBaseTitle>
            <LoadingContainer isLoading={loading}>
                {!!announcement && !isUseHelpCenterRefresh() && (
                    <AnnouncementBanner
                        actionSubjectId="portalAnnouncementBanner"
                        header={announcement.userLanguageHeader}
                        messageHtml={announcement.userLanguageMessageWiki}
                        InnerContainerComponent={AnnouncementInnerContainer}
                    />
                )}

                <PaperContent>
                    <UiModificationsEntryPoint portalId={portalId} requestTypeId={requestTypeId} />
                    <HeaderFocusMode portalId={portalId} requestTypeId={requestTypeId} />
                    <HeaderConnectFragments page={REQUEST_CREATE} portalId={portalId} requestTypeId={requestTypeId} />
                    <ForgeHeaderModule
                        page={FORGE_REQUEST_CREATE}
                        portalId={portalId}
                        projectId={projectId}
                        requestTypeId={requestTypeId}
                    />
                    <RequestCreateForm portalId={portalId} requestTypeId={requestTypeId} />
                </PaperContent>

                <FooterConnectFragments page={REQUEST_CREATE} portalId={portalId} requestTypeId={requestTypeId} />
                <PaperContent>
                    <ForgeFooterModule
                        page={FORGE_REQUEST_CREATE}
                        portalId={portalId}
                        projectId={projectId}
                        requestTypeId={requestTypeId}
                    />
                </PaperContent>
                {isProjectSimplified && isProjectAdmin && (
                    <LazySuspense fallback={null}>
                        <LazyPortalSettingsSidebar
                            isProjectSimplified={isProjectSimplified}
                            initialRoute={getPortalSettingsRequestTypeDetailOrphanRoute(requestTypeId)}
                            isOpen={sideBarOpen}
                            onOpen={openSidebar}
                            onClose={closeSidebar}
                        />
                    </LazySuspense>
                )}
            </LoadingContainer>
        </DocumentTitle>
    );
};

export default withErrorPage(injectIntl(RequestCreatePage));
